import { useEffect, useCallback } from 'react';
import { Dialog, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ensureHttps } from '@core/utils/formatting';
import type { ImageResponse } from '@core/types/ticket';

interface ImageLightboxProps {
  open: boolean;
  images: ImageResponse[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function ImageLightbox({
  open,
  images,
  currentIndex,
  onClose,
  onNavigate,
}: ImageLightboxProps) {
  const handlePrevious = useCallback(() => {
    onNavigate(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
  }, [currentIndex, images.length, onNavigate]);

  const handleNext = useCallback(() => {
    onNavigate(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
  }, [currentIndex, images.length, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, handlePrevious, handleNext]);

  if (!images.length) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
        },
      }}
      id="image-lightbox"
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        aria-label="Đóng"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: 'white',
          zIndex: 1,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Image & navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          position: 'relative',
          userSelect: 'none',
        }}
      >
        {/* Previous arrow */}
        {images.length > 1 && (
          <IconButton
            onClick={handlePrevious}
            aria-label="Ảnh trước"
            sx={{
              position: 'absolute',
              left: 16,
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        )}

        {/* Current image */}
        <img
          src={ensureHttps(images[currentIndex]?.file_url)}
          alt={`Ảnh ${currentIndex + 1}`}
          style={{
            maxWidth: '90%',
            maxHeight: '85vh',
            objectFit: 'contain',
          }}
        />

        {/* Next arrow */}
        {images.length > 1 && (
          <IconButton
            onClick={handleNext}
            aria-label="Ảnh tiếp"
            sx={{
              position: 'absolute',
              right: 16,
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        )}

        {/* Image counter */}
        <Typography
          variant="body2"
          sx={{
            position: 'absolute',
            bottom: 24,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            px: 2,
            py: 0.5,
            borderRadius: 2,
          }}
        >
          {currentIndex + 1} / {images.length}
        </Typography>
      </Box>
    </Dialog>
  );
}
